import { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Feature, PlanName, Subscription, Subscriptions, SubscriptionsState, UserPlan } from 'src/@types/feature';
import useMerchant from '../hooks/useMerchant';
import { getFeaturesAPI } from 'src/resources/api/features/index.api';
import { cancelSubscription, createUpdateToAnnualSaasSubscriptionLink, getSubscriptionPlans, getSubscriptionsApi, startTrialApi, updatePaymentMethod } from '../api/subscriptionsApi';
import { isPast } from 'date-fns';
import { getPlanLeftDays } from 'src/utils/dateTime';
// import { appcuesIdentifyMerchantSubscriptions, appcuesTrackEvent } from "../utils/appcues";
import useAuth from 'src/hooks/useAuth';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { BASE_URL } from "src/config";
import { createCheckoutSessionForSaasSubscription } from 'src/api/purchaseApi';
import { DEFAULT_CURRENCY } from 'src/utils/priceFormatter';

const SubscriptionsContext = createContext<SubscriptionsState>({} as SubscriptionsState);

type SubscriptionsProviderProps = {
  children: ReactNode;
};

const SubscriptionsContextProvider = ({ children }: SubscriptionsProviderProps) => {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [isFeaturesLoading, setIsFeaturesLoading] = useState<boolean>(true);
  const [subscriptions, setSubscriptions] = useState<Subscriptions>({
    currentSubscriptions: [],
    expiredSubscriptions: [],
    trialsAvailable: []
  });
  const [subscriptionsAreLoading, setSubscriptionsAreLoading] = useState<boolean>(true);
  const [subscriptionPlans, setSubscriptionPlans] = useState<UserPlan[]>([]);
  const [isPlansLoading, setIsPlansLoading] = useState(true);
  const [priceSavingForYear, setPriceSavingForYear] = useState({
    [PlanName.STARTER]: '',
    [PlanName.ADVANCED]: '',
  })
  const [planCurrency, setPlanCurrency] = useState('');

  const { merchant, isMerchantLoading } = useMerchant();
  const { user } = useAuth();

  const fetchFeatures = useCallback(async (merchantCode: string) => {
    getFeaturesAPI({ merchantCode: merchantCode })
      .then((result: any) => {
        setFeatures(result?.features);
        setIsFeaturesLoading(false);
      })
      .catch((response: any) => {
        console.error(response);
      })
      .finally(() => {
        setIsFeaturesLoading(false);
      })
  }, []);

  const fetchPlans = useCallback(async () => {
    setIsPlansLoading(true);
    const result = await getSubscriptionPlans();
    setSubscriptionPlans(result?.data?.content?.sort((v1: any, v2: any) => {
      return v1.planLevel - v2.planLevel;
    }))
    setIsPlansLoading(false);
  }, []);

  const availablePlanCurrencies = useMemo(() => {
    const list = subscriptionPlans?.find(plan => plan?.planLevel === PlanName.STARTER)?.prices;
    return [...new Set(list?.map(item => item?.currency?.toUpperCase()))]
  }, [subscriptionPlans]);

  const currentPaidPlan = useMemo(() => 
    subscriptions?.currentSubscriptions?.
      find((subscription: Subscription) => !subscription.trial && subscription?.planLevel > 10),
  [subscriptions?.currentSubscriptions]);

  useEffect(() => {
    if (merchant) {
      if (currentPaidPlan?.price?.currency) {
        setPlanCurrency(currentPaidPlan?.price?.currency);
      } else if (availablePlanCurrencies.includes(merchant.currency)) {
        setPlanCurrency(merchant.currency);
      } else {
        setPlanCurrency(DEFAULT_CURRENCY);
      }
    }
  }, [currentPaidPlan, merchant, isMerchantLoading, availablePlanCurrencies, availablePlanCurrencies?.length]);

  const fetchSubscriptions = useCallback(async (merchantCode: string) => {
    setSubscriptionsAreLoading(true);
    const result = await getSubscriptionsApi(merchantCode);
    const fetchedSubscriptions = result?.data;
    if(fetchedSubscriptions){
      setSubscriptions(fetchedSubscriptions);
      await fetchFeatures(merchantCode);
      // merchant && appcuesIdentifyMerchantSubscriptions(merchant?.id, fetchedSubscriptions)
    }
    setSubscriptionsAreLoading(false);
  }, [fetchFeatures]);

  const userOnFreePlan = useMemo(() : boolean =>
    subscriptions?.currentSubscriptions?.every(el => el.planLevel <= 10), // if all plans are less than 10
  [subscriptions?.currentSubscriptions]);

  const trialExpiresAt = useMemo(() =>
    subscriptions?.currentSubscriptions?.reverse().find((el: any) => el.trial && el.expiresAt)?.expiresAt,
    [subscriptions?.currentSubscriptions]
  );

  const daysLeft = useMemo(() =>
    trialExpiresAt && !isPast(new Date(trialExpiresAt))
      ? getPlanLeftDays(trialExpiresAt)
      : 0,
    [trialExpiresAt]
  );

  const trialIsAvailable = useMemo(() : boolean => {
    return subscriptions?.trialsAvailable?.length > 0
  },[subscriptions?.trialsAvailable?.length]);

  const showStartTrialMessage = useMemo(() =>
    userOnFreePlan && trialIsAvailable,
    [userOnFreePlan, trialIsAvailable]
  );

  const activateYourPlansMessage = useMemo(() =>
    userOnFreePlan && !trialIsAvailable,
    [userOnFreePlan, trialIsAvailable]
  );

  const isPremiumUser = useMemo((): boolean => {
    return subscriptions?.currentSubscriptions?.some((subscription) => 
    !subscription.trial && subscription?.planLevel > 10);
  }, [subscriptions?.currentSubscriptions]);

  const isCurrentPlanEnterprise = useMemo(() =>
    !!subscriptions?.currentSubscriptions?.find(el => el?.planLevel === PlanName.ENTERPRISE && !el?.trial)
      && subscriptions?.currentSubscriptions?.length === 1,
  [subscriptions?.currentSubscriptions]);

  const trialExpiresMessage = useMemo(() =>
          !!trialExpiresAt && !isPremiumUser,
      [trialExpiresAt, isPremiumUser]
  );

  const startTrial = useCallback(async () => {
    if(trialIsAvailable && merchant?.code) {
      setSubscriptionsAreLoading(true);
      const trialSubscription : Subscription = {
        planLevel: subscriptions.trialsAvailable[0].planLevel,
      }
      const result = await startTrialApi(merchant.code, trialSubscription);
      if (result?.data) {
        // appcuesTrackEvent("user_started_trial");
        fetchSubscriptions(merchant?.code)
            .then(() => {
              setSubscriptionsAreLoading(false);
            });
        fetchFeatures(merchant?.code);
      }
    }
  }, [trialIsAvailable, merchant?.code, subscriptions.trialsAvailable, fetchSubscriptions, fetchFeatures]);

  useEffect(() => {
    if(merchant?.code) {
      fetchFeatures(merchant.code);
      fetchSubscriptions(merchant.code);
      fetchPlans();
    }
  }, [fetchFeatures, fetchSubscriptions, fetchPlans, merchant?.code]);

  const getPlanByPlanLevel = (planLevel: number) => {
    if (subscriptionPlans) {
      return subscriptionPlans?.find(plan => plan?.planLevel === planLevel);
    }
  }

  const onSubscribeButtonClick = (id: string, planLevel: number) => {
    if(!user?.email || !merchant?.code || !id) {
      return;
    }

    if (!isPremiumUser) {
      createCheckoutSessionForSaasSubscription(merchant?.code, {
        customerEmail: user.email,
        priceId: id,
        successUrl: `${BASE_URL}${PATH_DASHBOARD.user.confirmation}?expected_level=${planLevel}`,
        cancelUrl: `${BASE_URL}${PATH_DASHBOARD.user.plans}`
      })
        .then(({ data }) => {
          window.location.href = data.checkoutUrl;
        })
    } else {
      createUpdateToAnnualSaasSubscriptionLink(merchant?.code, {
        priceId: id,
        successUrl: `${BASE_URL}${PATH_DASHBOARD.user.confirmation}?expected_level=${planLevel}`,
        cancelUrl: `${BASE_URL}${PATH_DASHBOARD.user.plans}`
      })
      .then(({ data }) => {
        window.location.href = data.url;
      })
    }
  }

  const onUpdateToAnnualButtonClick = (planLevel: number, currency: string, interval: string) => {
    const planPrices = getPlanByPlanLevel(planLevel)?.prices;
    const yearlyPlanId = planPrices?.find(price => 
      price?.recurringInterval === interval && price?.currency === currency?.toLowerCase());

    if(!user?.email || !merchant?.code || !yearlyPlanId) {
      return;
    }

    const data = {
      priceId: yearlyPlanId.id,
      successUrl: `${BASE_URL}${PATH_DASHBOARD.user.confirmation}`,
      cancelUrl: `${BASE_URL}${PATH_DASHBOARD.user.plans}`
    }

    createUpdateToAnnualSaasSubscriptionLink(merchant?.code, data)
      .then(({ data }) => {
        window.location.href = data.url;
      })
  }

  const onCancelButtonClick = () => {
    if(!user?.email || !merchant?.code) {
      return;
    }
    const data = {
      successUrl: `${BASE_URL}${PATH_DASHBOARD.user.cancelConfirmation}`,
      cancelUrl: `${BASE_URL}${PATH_DASHBOARD.user.plans}`
    }
    cancelSubscription(merchant?.code, data)
      .then(({ data }) => {
        console.log(data);
        window.location.href = data.url;
      })
  }

  const onUpdatePaymentMethod = async (): Promise<void> => {
    if(user?.email && merchant?.code) {
      const request = {
        successUrl: `${BASE_URL}${PATH_DASHBOARD.user.confirmation}`,
        cancelUrl: `${BASE_URL}${PATH_DASHBOARD.user.plans}`
      }
      const { data } = await updatePaymentMethod(merchant?.code, request);
      window.location.href = data.url;
    }
  }

  const isStripeSubscription = useMemo((): boolean => {
    return subscriptions?.currentSubscriptions?.some((subscription) => 
    subscription?.externalProviderCode !== 'STRIPE' && subscription?.planLevel > 10 && !subscription.trial);
  }, [subscriptions?.currentSubscriptions]);

  return (
    <SubscriptionsContext.Provider
      value={{
        features,
        isFeaturesLoading,
        subscriptions,
        trialExpiresAt,
        daysLeft,
        showStartTrialMessage,
        trialExpiresMessage,
        activateYourPlansMessage,
        startTrial,
        subscriptionsAreLoading,
        userOnFreePlan,
        isPremiumUser,
        isCurrentPlanEnterprise,
        subscriptionPlans,
        isPlansLoading,
        setPriceSavingForYear,
        priceSavingForYear,
        getPlanByPlanLevel,
        onSubscribeButtonClick,
        onUpdateToAnnualButtonClick,
        onCancelButtonClick,
        onUpdatePaymentMethod,
        planCurrency,
        setPlanCurrency,
        fetchSubscriptions,
        isStripeSubscription,
        availablePlanCurrencies,
      }}
    >
      {children}
    </SubscriptionsContext.Provider>
  );
}

export { SubscriptionsContext, SubscriptionsContextProvider };
